import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
//@ts-ignore
import FileIcon from "@assets/images/file.png";
import CustomInput from "@components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import { Search } from "lucide-react";
import { getJobRequirement } from "@endpoints/hiringCompany/requirements";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import dayjs from "dayjs";
import { getJobStatusLabel } from "@utils/helperFunction";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import { useDebounce } from "hooks/useDebounce";

const PartnerCompanyRequirements: React.FC = () => {
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
  } = useForm();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const hasData = data?.length > 0;

  const debouncedSearchText = useDebounce(searchText);

  useEffect(() => {
    fetchJobRequirements();
  }, [debouncedSearchText]);

  const fetchJobRequirements = useCallback(async () => {
    try {
      const response = await getJobRequirement({
        jobTitle: debouncedSearchText,
      });
      //@ts-ignore
      setData(response?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchText]);

  if (isLoading) {
    return (
      <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {hasData ? (
        <div className="flex flex-col min-h-0 bg-white flex-1">
          <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg p-2 mb-2">
            <div className="relative w-1/3">
              <CustomInputV2
                name="search"
                className="border-none"
                placeholder="Search by job title"
                inputContainerStyle="bg-white h-8"
                value={searchText}
                onChange={(e) => setSearchText(e?.target?.value)}
                renderRight={<Search color="#6F6F6F" width={16} />}
              />
            </div>
          </div>
          <div className="flex flex-col min-h-0 overflow-y-scroll no-scrollbar">
            <table className="min-w-full text-xs text-left">
              <thead className="border-b border-[#E0E0E0] sticky top-0 bg-white z-10">
                <tr className="font-semibold">
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Job title
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Published date
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Hiring Company
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Status
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Applied candidates
                  </th>
                  <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {data?.map((job, idx) => (
                  <tr
                    key={idx}
                    className="border-t  border-gray-100 cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/requirement-details/${job?._id}`)}
                  >
                    <td className="py-6 text-xs font-medium text-[#6F6F6F] ">
                      {job.title}
                    </td>
                    <td className="py-6 text-xs font-medium text-[#6F6F6F]">
                      {dayjs(job.createdAt).format("Do MMMM YYYY")}
                    </td>
                    <td className="py-6 text-xs text-[#6F6F6F] font-medium">
                      {job?.company?.name || "-"}
                    </td>
                    <td className="py-6 text-xs text-[#6F6F6F] capitalize">
                      {getJobStatusLabel(job.status)}
                    </td>
                    <td className="flex py-4 text-xs text-[#6F6F6F]">
                      <div className="relative inline-flex ml-10 self-center items-center justify-center w-8 h-8 rounded-full border border-gray-400 text-sm">
                        {job.totalSubmittedCandidates ?? 0}
                        {job.applied > 0 && (
                          <span className="absolute -top-1 -right-1 text-[8px] w-4 h-4 justify-center items-center flex text-white font-bold bg-green-500 rounded-full border-2 border-white">
                            2
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-6 text-xs text-[#6F6F6F]">{job.refNo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          <img src={FileIcon} alt="File Icon" />
          <p className="text-xs text-[#6F6F6F] font-medium mt-4">
            You currently don’t have any proposed candidates.
          </p>
          <p className="text-sm text-primary font-semibold mt-6 whitespace-pre">
            You can submit your job requirement by{"\n"}clicking on the below
            button.
          </p>
        </div>
      )}
    </div>
  );
};

export default PartnerCompanyRequirements;
